import { useCallback, useState } from 'react';
import {
  useCompleteUploadMutation,
  useInitUploadMutation,
} from '@/state/slices/uploads';
import { prepareUploadPayload } from '../utils/zipFiles';

export type UploadStage =
  | 'idle'
  | 'preparing'
  | 'requesting-url'
  | 'uploading'
  | 'success'
  | 'error';

export interface UploadState {
  stage: UploadStage;
  progress: number;
  errorMessage?: string;
}

interface UploadArgs {
  files: File[];
}

const putToSignedUrl = (
  url: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `GCS upload failed (${xhr.status}): ${xhr.responseText || xhr.statusText}`
          )
        );
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.send(file);
  });

/**
 * Two-step upload orchestration:
 *   1. POST /api/v1/uploads/init/ {filename} on ai-pacs-be → returns signed URL.
 *   2. PUT the file binary directly to the signed URL.
 *   3. POST /api/v1/uploads/<uploadId>/complete/ (best effort).
 *
 * No DICOM parsing, no patient form data — ai-pacs-be brokers the URL from
 * the ingestion service (which holds the API key server-side) and tracks the
 * upload via its own Upload model. The actual file bytes never proxy through
 * ai-pacs-be; PUT goes directly to GCS.
 */
export function useUploadStudy() {
  const [state, setState] = useState<UploadState>({
    stage: 'idle',
    progress: 0,
  });
  const [initUpload] = useInitUploadMutation();
  const [completeUpload] = useCompleteUploadMutation();

  const upload = useCallback(
    async ({ files }: UploadArgs) => {
      setState({ stage: 'preparing', progress: 0 });
      try {
        // Multi-file drops get bundled client-side via JSZip into one archive
        // before upload. Single .dcm or single .zip pass through unchanged.
        const payload = await prepareUploadPayload(files);

        setState({ stage: 'requesting-url', progress: 0 });
        const init = await initUpload({ filename: payload.name }).unwrap();

        setState({ stage: 'uploading', progress: 0 });
        await putToSignedUrl(init.uploadUrl, payload, (pct) =>
          setState({ stage: 'uploading', progress: pct })
        );

        // Records when the file finished sending. A failure here never fails
        // the upload — the bytes are already in GCS.
        try {
          await completeUpload(init.uploadId).unwrap();
        } catch {
          // Swallowed on purpose.
        }

        setState({ stage: 'success', progress: 100 });
        return { uploadId: init.uploadId, objectPath: init.objectPath };
      } catch (err) {
        // RTK Query mutation errors come through as { status, data, error? }.
        const rtkErr = err as {
          status?: number | string;
          data?: { detail?: string; error?: string };
          error?: string;
        };
        const message =
          (err instanceof Error && err.message) ||
          rtkErr?.data?.detail ||
          rtkErr?.data?.error ||
          rtkErr?.error ||
          'Upload failed';
        setState({ stage: 'error', progress: 0, errorMessage: message });
        throw err;
      }
    },
    [initUpload, completeUpload]
  );

  const reset = useCallback(() => setState({ stage: 'idle', progress: 0 }), []);

  return { upload, reset, state };
}
